import { Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { UploadClientDetailsDialog } from '../client-details/client-details.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { VendorService } from '../../features/vendors/services/vendor.service';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';

@Component({
  selector: 'app-vendors',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './vendors.component.html',
  styleUrl: './vendors.component.scss',
})
export class VendorsComponent {
  public viewPortSize: boolean = false;
  search: any;
  public rowData: any[] | null = null;
  readonly dialog = inject(MatDialog);
  searchInputControl: FormControl = new FormControl();
  columnDefs: (
    | {
        headerName: string;
        field: string;
        flex: number;
        cellRenderer: (params: any) => string;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        headerName: string;
        field: string;
        flex: number;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        field: string;
        headerName: string;
        flex: number;
        cellRenderer: (params: any) => HTMLDivElement;
        floatingFilter: boolean;
        filter: boolean;
      }
  )[];
  canAddVendor$ = this.permissionService.hasPermission(
    ModuleIds.Vendor,
    'Create'
  );

  canUploadVendor$ = this.permissionService.hasPermission(
    ModuleIds.Vendor,
    'Upload'
  );

  canDeleteVendor$ = this.permissionService.hasPermission(
    ModuleIds.Vendor,
    'Delete'
  );

  canEditVendor$ = this.permissionService.hasPermission(
    ModuleIds.Vendor,
    'Edit'
  );

  canDeleteVendor: boolean = false;
  canEditVendor: boolean = false;

  constructor(
    private router: Router,
    private vendorService: VendorService,
    private toastr: ToastrService,
    private permissionService: PermissionService,
  ) // private store: Store
  {}

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  openDialog() {
    const dialogRef = this.dialog.open(UploadClientDetailsDialog, {
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.fetchVendors();
    });
  }

  ngOnInit() {
    this.fetchVendors();
    this.searchInputControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.search = value;
        this.fetchVendors();
      });

      this.canDeleteVendor$.subscribe(val => {
        this.canDeleteVendor = val;
      });

      this.canEditVendor$.subscribe(val => {
        this.canEditVendor = val;
      });

    this.checkViewportSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        headerName: 'Vendor Name',
        field: 'vendorName',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          return `
          <div style="display: flex; align-items: center;">
            <span>${params.data.vendorName}</span>
          </div>
        `;
        },
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Phone No',
        field: 'phone',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Currency',
        field: 'currency',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Contact Person',
        field: 'contactPerson', 
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Address',
        field: 'address',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.gap = '20px';

          const editIcon = document.createElement('img');
          editIcon.src = 'assets/images/icons/icon-edit.svg';
          editIcon.style.cursor = 'pointer';
          editIcon.setAttribute('data-action', 'edit');

          if(this.canEditVendor){ 
          container.appendChild(editIcon);
          }

          const deleteIcon = document.createElement('img');
          deleteIcon.src = 'assets/images/icons/icon-delete.svg';
          deleteIcon.style.cursor = 'pointer';
          deleteIcon.setAttribute('data-action', 'delete');

          if(this.canDeleteVendor){
            container.appendChild(deleteIcon);
          }

          return container;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  public themeClass: string = 'ag-theme-quartz';

  onCellClicked(params: any) {
    const vendorId = params.data?.id;
    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;

      if (action === 'edit') {
        this.onEdit(vendorId);
      } else if (action === 'delete') {
        this.onConfirmDelete(vendorId);
      }
    } else {
      this.router.navigate(['/vendors/vendor-details'], {
        // queryParams: { id: vendorId, partners: this.rowData},
        queryParams: { id: vendorId },
      });
    }
  }

  fetchVendors() {
    this.vendorService.fetchVendors(this.search).subscribe({
      next: (data: any) => {
        this.rowData = data.map((vendor: any) => {
          return {
            id: vendor?.id ?? '--',
            vendorName: vendor?.vendorName ?? '--',
            email: vendor?.email ?? '--',
            phone: vendor?.phone ?? '--',
            currency: vendor?.currency ?? '--',
            address: vendor?.address ?? '--',
            contactPerson: vendor?.contactPerson ?? '--',
          };
        });
      },
      error: (error) => {
        this.toastr.error(error?.message || 'Failed to fetch vendors');
      },
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.search = input.value;
    this.fetchVendors();
  }

  onConfirmDelete(id: number): void {
    console.log(id);

    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onDelete(id);
      }
    });
  }

  onDelete(id: any) {
    this.vendorService.removeVendor(id).subscribe({
      next: (result) => {
        if (result) {
          this.toastr.success('Vendor deleted successfully', 'Success');
          this.fetchVendors();
        }
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      },
    });
  }

  onEdit(id: any) {
    this.router.navigate([`/vendors/vendors-edit/${id}`]);
  }
}

@Component({
  selector: 'upload-clients-dialog',
  templateUrl: 'upload-vendors-dialog.html',
  styleUrls: ['./vendors.component.scss'],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
})
export class UploadClientsDialog {
  file: any = null;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log('Selected file:', file);
      this.file = file;
    }
  }
}
